import { MouseEvent, useState } from 'react'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Stack, Typography } from '@mui/material'
import { CircleFlag } from '@/components/CircleFlag'
import { territories } from '@/components/forms/GeoBlockingTemplateForm/GeoBlockingTemplateForm.constants'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'

import { StreamingCountrySelectPopper } from './StreamingCountrySelectPopper'

type Props = {
  value: string[]
  onChange: (value: string[]) => void
  label?: string
  disabled?: boolean
  maxFlagsDisplay?: number
  whitelist?: boolean
}

export const StreamingCountrySelect = ({
  onChange,
  value,
  disabled,
  label,
  maxFlagsDisplay = 8,
  whitelist
}: Props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

  const handleClickMenuTrigger = (event: MouseEvent<HTMLElement>) => {
    if (menuAnchorEl || disabled) {
      setMenuAnchorEl(null)
      return
    }

    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleRegionsChange = (regionCodes: string[]) => {
    onChange(regionCodes)
  }

  const selectedRegions = value
    .map((code) => territories.find((territory) => territory.iso2 === code))
    .filter((region) => region !== undefined)

  const sortedRegions = [...selectedRegions].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder
    }
    return a.displayLabel.localeCompare(b.displayLabel)
  })

  const displayedRegions = sortedRegions.slice(0, maxFlagsDisplay)
  const remainingCount = sortedRegions.length - maxFlagsDisplay

  const renderCustomInput = () => {
    if (value.length === 0) {
      return null
    }

    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          py: 0.5,
          ml: -0.5,
          minHeight: 24
        }}
      >
        {displayedRegions.map((region) => (
          <Box
            key={region.iso2}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            <CircleFlag countryCode={region.iso2.toLowerCase()} height="100%" width="100%" />
          </Box>
        ))}

        {remainingCount > 0 && (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: 'text.secondary'
              }}
            >
              +{remainingCount}
            </Typography>
          </Box>
        )}
      </Stack>
    )
  }

  return (
    <>
      <ConsoleTextField
        label={label || 'Select Territories'}
        onClick={handleClickMenuTrigger}
        disabled={disabled}
        type="button"
        placeholder={value.length === 0 ? 'Select territories...' : undefined}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: disabled ? 'default' : 'pointer'
          },
          opacity: disabled ? 0.5 : 1
        }}
        slotProps={{
          input: {
            'aria-expanded': Boolean(menuAnchorEl),
            'aria-haspopup': 'dialog',
            readOnly: true,
            startAdornment: value.length > 0 ? renderCustomInput() : undefined,
            endAdornment: (
              <ArrowDropDownIcon
                color="action"
                sx={{
                  transform: menuAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                  cursor: 'pointer',
                  mr: -0.75,
                  opacity: disabled ? 0.5 : 1
                }}
              />
            )
          }
        }}
      />
      <StreamingCountrySelectPopper
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        value={value}
        whitelist={whitelist}
        onChange={handleRegionsChange}
      />
    </>
  )
}
